import { useMemo, useState, useEffect } from "react";
import { api, formatApiErrorDetail, num } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { ArrowDown } from "lucide-react";

export default function CreateEntryDialog({ open, onClose, onSaved, accounts, existing }) {
  const [from, setFrom] = useState(existing?.from_account_id || "");
  const [to, setTo] = useState(existing?.to_account_id || "");
  const [amount, setAmount] = useState(existing ? String(existing.amount) : "");
  const [note, setNote] = useState(existing?.note || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (existing) {
      setFrom(existing.from_account_id);
      setTo(existing.to_account_id);
      setAmount(String(existing.amount));
      setNote(existing.note || "");
    }
  }, [existing]);

  const fromAcc = useMemo(() => accounts.find((a) => a.id === from), [accounts, from]);
  const toAcc = useMemo(() => accounts.find((a) => a.id === to), [accounts, to]);
  const amt = parseFloat(amount);
  const validAmt = !isNaN(amt) && amt !== 0;

  const previewFrom = fromAcc && validAmt ? fromAcc.balance - amt : fromAcc?.balance;
  const previewTo = toAcc && validAmt ? toAcc.balance + amt : toAcc?.balance;

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!from || !to || from === to) {
      setErr("Choose two different accounts");
      return;
    }
    if (!validAmt) {
      setErr("Enter a non-zero amount");
      return;
    }
    setBusy(true);
    try {
      if (existing) {
        await api.patch(`/entries/${existing.id}`, {
          from_account_id: from,
          to_account_id: to,
          amount: amt,
          note,
        });
      } else {
        await api.post("/entries", {
          from_account_id: from,
          to_account_id: to,
          amount: amt,
          note,
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const balanceClass = (v) =>
    v > 0 ? "text-emerald-700" : v < 0 ? "text-rose-700" : "text-stone-600";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-strong rounded-3xl border-white/80 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {existing ? "Edit entry" : "New entry"}
          </DialogTitle>
          <DialogDescription>
            Money flows from the top account to the bottom account. Enter a negative amount to
            reverse.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4">
          {/* From */}
          <div className="glass rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-tiny">From (top)</span>
              {fromAcc && (
                <span className={`text-xs ${balanceClass(fromAcc.balance)}`}>
                  Balance: <b className="font-serif text-sm">{num(fromAcc.balance)}</b>
                </span>
              )}
            </div>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger className="w-full rounded-xl bg-white/80" data-testid="from-account-select">
                <SelectValue placeholder="Choose account…" />
              </SelectTrigger>
              <SelectContent className="glass-strong rounded-xl">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}{" "}
                    <span className="text-xs text-stone-500 ml-2">({num(a.balance)})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fromAcc && validAmt && (
              <div className="text-xs mt-2 text-stone-500">
                After: <span className={`font-serif text-base ${balanceClass(previewFrom)}`}>{num(previewFrom)}</span>
              </div>
            )}
          </div>

          {/* Arrow + amount */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-stone-200" />
            <div className="w-10 h-10 rounded-full glass-gold flex items-center justify-center">
              <ArrowDown size={16} className="text-[color:var(--gold-deep)]" />
            </div>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          {/* Amount */}
          <div>
            <label className="label-tiny">Amount (use - for reverse)</label>
            <input
              type="number"
              step="any"
              autoFocus
              required
              data-testid="entry-amount-input"
              className="w-full mt-1 px-4 py-4 rounded-2xl bg-white/80 border border-white/90 focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 font-serif text-3xl text-center"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* To */}
          <div className="glass rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-tiny">To (bottom)</span>
              {toAcc && (
                <span className={`text-xs ${balanceClass(toAcc.balance)}`}>
                  Balance: <b className="font-serif text-sm">{num(toAcc.balance)}</b>
                </span>
              )}
            </div>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger className="w-full rounded-xl bg-white/80" data-testid="to-account-select">
                <SelectValue placeholder="Choose account…" />
              </SelectTrigger>
              <SelectContent className="glass-strong rounded-xl">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}{" "}
                    <span className="text-xs text-stone-500 ml-2">({num(a.balance)})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {toAcc && validAmt && (
              <div className="text-xs mt-2 text-stone-500">
                After: <span className={`font-serif text-base ${balanceClass(previewTo)}`}>{num(previewTo)}</span>
              </div>
            )}
          </div>

          <div>
            <label className="label-tiny">Note (optional)</label>
            <input
              type="text"
              data-testid="entry-note-input"
              className="w-full mt-1 px-4 py-3 rounded-2xl bg-white/70 border border-white/80 focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., loan repayment"
            />
          </div>

          {err && (
            <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2">
              {err}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-gold flex-1"
              data-testid="entry-save-btn"
            >
              {busy ? "Saving…" : existing ? "Save changes" : "Post entry"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
